import { MDBCheckbox, MDBIcon, MDBInput, MDBTextArea } from "mdb-react-ui-kit";
import { useContext, useEffect } from 'react';
import useResumeExperiencesHandlers from "../../../context/resume/hooks/resume-experiences-handlers";
import { ResumeContext } from "../../../context/resume/resume-context";
import { TranslationContext } from "../../../context/translations/translations-context";
import { isoDurationToDays } from "../../../utils/duration-utils";
import calculateNextMonthDate from "../../assets/CalculateNextMonthDate";
import MyMonthInput from "../../assets/MyMonthInput";
import ReferentialSelect from "../../assets/ReferentialSelect";
import AddExperienceAction from "../experiences-actions/AddExperienceAction";
import ExperienceActionList from "../experiences-actions/ExperienceActionList";
import AddExperienceRole from "../experiences-roles/AddExperienceRole";
import ExperienceRoleList from "../experiences-roles/ExperienceRoleList";
import TechnicalEnvironment from "./tech-env/TechnicalEnvironment";

export default function ExperienceItem(
    {
        item,
        refActivitySectors
    }
) {
    const { translations, displayLanguage } = useContext( TranslationContext );
    const { refContractTypes } = useContext( ResumeContext );
    const { handleChange, handleRemove } = useResumeExperiencesHandlers();

    useEffect( () => {
        if ( item.duration && typeof item.duration !== 'number') {
            console.log(`Converting ${item.duration}`);
            handleChange( {
                ...item,
                duration: isoDurationToDays( item.duration || "0" ),
            } );
        }
    }, [item, handleChange] );

    const minimumDateForDateTo = calculateNextMonthDate( item.from );

    function handleChangeTechnicalEnvironment( technicalEnvironment ) {
        handleChange( { ...item, technicalEnvironment: technicalEnvironment } )
    }

    return (
        <div>
            <div className="row">
                <div className="d-flex align-items-center justify-content-end">
                    <MDBIcon
                        far
                        icon="trash-alt"
                        className={ `m-1 text-end` }
                        onClick={ () => window.confirm(translations.general.confirmationMessage) && handleRemove( item ) }
                        role="button"
                        size="lg"
                    />
                </div>
                <div className="col-4">
                    <div>
                        <label>{ translations.experiencesSection.employerLabel }</label>
                        <MDBInput
                            type="text"
                            className="form-control-input"
                            value={ item.employer || "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, employer: event.target.value } )
                            }
                        />
                    </div>
                    <div>
                        <span
                            title={ translations.experiencesSection.activitySectorTooltip +
                                '\n' + translations.experiencesSection.notInEuropassTooltip }
                        >
                            <label>{ translations.experiencesSection.activitySectorLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <ReferentialSelect
                            name="activitySector"
                            elementId={ `activitySector-${ item.order }` }
                            firstOption={ translations.experiencesSection.selectActivitySectorLabel }
                            selectedItem={ item.activitySector?.id || "" }
                            referential={ refActivitySectors }
                            displayLanguage={ displayLanguage }
                            className={ `form-select select-input ${ !item.activitySector ? 'border-danger' : '' }` }
                            handleChange={
                                ( event ) =>
                                    handleChange( {
                                        ...item,
                                        activitySector: refActivitySectors.find( el => el.id === Number.parseInt( event.target.value ) )
                                    } )
                            }
                        />
                    </div>
                    <div>
                        <label>{ translations.experiencesSection.locationLabel }</label>
                        <MDBInput
                            type="text"
                            className="form-control-input"
                            value={ item.location || "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, location: event.target.value } )
                            }
                        />
                    </div>
                    <div className="label-input-container">
                        <label>{ translations.experiencesSection.startDateLabel }</label>
                        <MyMonthInput
                            className="transparent-input"
                            value={ item?.from ?? "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, from: event.target.value } )
                            }
                        />
                        <label>{ translations.experiencesSection.endDateLabel }</label>
                        <MyMonthInput
                            className={ `transparent-input ${ item.isCurrent ? 'disabled-input' : '' }` }
                            value={ item?.to ?? "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, to: event.target.value } )
                            }
                            placeholder="Date de fin"
                            min={ minimumDateForDateTo ? minimumDateForDateTo.toISOString().split( 'T' )[ 0 ]
                                : undefined }
                            disabled={ item?.isCurrent ?? false }
                        />
                        <div>
                            <input
                                id={ `currentExperience-${ item.order }` }
                                type="checkbox"
                                onChange={
                                    ( event ) =>
                                        handleChange( { ...item, isCurrent: event.target.checked } )
                                }
                                checked={ item?.isCurrent ?? false }
                            />
                            <label htmlFor={ `currentExperience-${ item.order }` }>{ translations.general.now }</label>
                        </div>
                    </div>
                </div>
                <div className="col-8">
                    <div>
                        <label>{ translations.experiencesSection.jobPositionLabel }</label>
                        <MDBInput
                            type="text"
                            className="form-control-input"
                            value={ item.jobPosition || "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, jobPosition: event.target.value } )
                            }
                        />
                        <MDBCheckbox
                            name='flexCheck'
                            value=''
                            id={ `flexCheckDefault-${ item.order }` }
                            label="S'agit-il d'une mission de consulting"
                            checked={ item.isConsultingMission ?? false }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, isConsultingMission: event.target.checked } )
                            }
                        />
                        { item.isConsultingMission && (
                            <div>
                                <label>Client</label>
                                <MDBInput
                                    type="text"
                                    className="form-control-input"
                                    placeholder="Client"
                                    value={ item.client || "" }
                                    onChange={
                                        ( event ) =>
                                            handleChange( { ...item, client: event.target.value } )
                                    }
                                />
                            </div>
                        ) }
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.notInEuropassTooltip }>
                            <label>{ translations.experiencesSection.contractTypeLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <ReferentialSelect
                            name="contractType"
                            elementId={ `contractType-${ item.order }` }
                            firstOption={ translations.experiencesSection.selectContractTypeLabel }
                            selectedItem={ item.contractType?.id || "" }
                            referential={ refContractTypes }
                            displayLanguage={ displayLanguage }
                            className={ `form-select select-input ${ !item.contractType ? 'border-danger' : '' }` }
                            handleChange={
                                ( event ) =>
                                    handleChange( {
                                        ...item,
                                        contractType: refContractTypes.find( el => el.id === Number.parseInt( event.target.value ) )
                                    } )
                            }
                        />
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.inEuropassTooltip }>
                            <label>{ translations.experiencesSection.durationLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <MDBInput
                            type="number"
                            className="form-control-input"
                            value={ item.duration || 0 }
                            onChange={
                                ( event ) =>
                                    handleChange( {
                                        ...item,
                                        duration: +event.target.value,
                                    } )
                            }
                        />
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.inEuropassTooltip }>
                            <label>{ translations.experiencesSection.rolesLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <div>
                            <ExperienceRoleList
                                items={ item.roles || [] }
                                numberOfItems={ item.roles?.length || 0 }
                                parentOrder={ item.order }
                            />
                            <AddExperienceRole parentOrder={ item.order }/>
                        </div>
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.contextTooltip }>
                            <label>{ translations.experiencesSection.contextLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <MDBTextArea
                            type="text"
                            className="form-control-input"
                            value={ item.context || "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, context: event.target.value } )
                            }
                        />
                    </div>
                    <div>
                        <span
                            title={ translations.experiencesSection.achievementsTooltip
                                + ' ' + translations.experiencesSection.notInEuropassTooltip }
                        >
                            <label>{ translations.experiencesSection.achievementsLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <MDBTextArea
                            type="text"
                            className="form-control-input"
                            value={ item.achievement || "" }
                            onChange={
                                ( event ) =>
                                    handleChange( { ...item, achievement: event.target.value } )
                            }
                        />
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.actionsTooltip }>
                            <label>{ translations.experiencesSection.actionsLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <div>
                            <ExperienceActionList
                                items={ item.actions || [] }
                                numberOfItems={ item.actions?.length || 0 }
                                parentOrder={ item.order }
                            />
                            <AddExperienceAction parentOrder={ item.order }/>
                        </div>
                    </div>
                    <div>
                        <span title={ translations.experiencesSection.techEnvironmentTooltip }>
                            <label>{ translations.experiencesSection.techEnvironmentLabel }</label>
                            <i className="fas fa-info-circle ms-1"></i>
                        </span>
                        <TechnicalEnvironment
                            value={ item.technicalEnvironment || [] }
                            handleChangeTechnicalEnvironment={ handleChangeTechnicalEnvironment }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
